import React, { memo, useCallback, useMemo, useState } from 'react';

import { Button, IconButton, Popover, Stack, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import DateFieldCDS from '@sharedCR/coreDesignSystem/form/DateFieldCDS';
import { MenuIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import usePopover from 'hooks/usePopover';

import {
  anchorOrigin,
  dateRangeContentStyles,
  dateRangeFieldStyles,
  dateRangeSeparatorStyles,
  filterIconButtonStyles,
  menuControlStyles,
  popoverSlotProps,
  transformOrigin,
} from './ColumnDateFilterMenu.styles';
import { ColumnDateFilterMenuTestIds } from './ColumnDateFilterMenu.testIds';

const ColumnDateFilterMenu = ({
  columnKey,
  filterStart = null,
  filterEnd = null,
  onFilterChange,
  externalAnchorEl,
  onClose: onExternalClose,
}) => {
  const { anchorEl: internalAnchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const isControlled = !!externalAnchorEl;
  const anchorEl = isControlled ? externalAnchorEl : internalAnchorEl;

  const [localStart, setLocalStart] = useState(filterStart);
  const [localEnd, setLocalEnd] = useState(filterEnd);

  const isOpen = !!anchorEl;
  const isFilterActive = !!filterStart || !!filterEnd;

  const iconButtonClassName = useMemo(
    () => `column-filter-icon${isFilterActive ? ' filter-active' : ''}`,
    [isFilterActive],
  );

  const handleOpen = useCallback(
    e => {
      e.stopPropagation();
      setLocalStart(filterStart);
      setLocalEnd(filterEnd);
      onSetAnchorElement(e);
    },
    [filterStart, filterEnd, onSetAnchorElement],
  );

  const closePopover = useCallback(() => {
    if (isControlled) {
      onExternalClose();

      return;
    }

    onClearAnchorElement();
  }, [isControlled, onExternalClose, onClearAnchorElement]);

  const handleClose = useCallback(() => {
    setLocalStart(filterStart);
    setLocalEnd(filterEnd);
    closePopover();
  }, [filterStart, filterEnd, closePopover]);

  const handleStartChange = useCallback(value => {
    setLocalStart(value);
  }, []);

  const handleEndChange = useCallback(value => {
    setLocalEnd(value);
  }, []);

  const handleConfirm = useCallback(() => {
    onFilterChange(columnKey, { start: localStart, end: localEnd });
    closePopover();
  }, [columnKey, localStart, localEnd, onFilterChange, closePopover]);

  const handleClear = useCallback(() => {
    setLocalStart(null);
    setLocalEnd(null);
    onFilterChange(columnKey, { start: null, end: null });
    closePopover();
  }, [columnKey, onFilterChange, closePopover]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {!isControlled && (
        <IconButton
          disableRipple
          className={iconButtonClassName}
          data-testid={ColumnDateFilterMenuTestIds.TRIGGER}
          size="small"
          sx={filterIconButtonStyles}
          onClick={handleOpen}
        >
          <MenuIcon size={IconSize[16]} />
        </IconButton>
      )}
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        open={isOpen}
        slotProps={popoverSlotProps}
        transformOrigin={transformOrigin}
        onClose={handleClose}
      >
        <Stack sx={dateRangeContentStyles}>
          <DateFieldCDS
            clearable
            dataTestId={ColumnDateFilterMenuTestIds.START_FIELD}
            label="Start"
            maxDate={localEnd}
            sx={dateRangeFieldStyles}
            value={localStart}
            onBlur={_.noop}
            onChange={handleStartChange}
            onFocus={_.noop}
          />
          <Typography sx={dateRangeSeparatorStyles} variant="regular14">
            -
          </Typography>
          <DateFieldCDS
            clearable
            dataTestId={ColumnDateFilterMenuTestIds.END_FIELD}
            label="End"
            minDate={localStart}
            sx={dateRangeFieldStyles}
            value={localEnd}
            onBlur={_.noop}
            onChange={handleEndChange}
            onFocus={_.noop}
          />
        </Stack>
        <Stack sx={menuControlStyles}>
          <Button
            data-testid={ColumnDateFilterMenuTestIds.CLEAR_BUTTON}
            variant="line"
            onClick={handleClear}
          >
            Clear
          </Button>
          <Button data-testid={ColumnDateFilterMenuTestIds.CONFIRM_BUTTON} onClick={handleConfirm}>
            Confirm
          </Button>
        </Stack>
      </Popover>
    </LocalizationProvider>
  );
};

ColumnDateFilterMenu.propTypes = {
  columnKey: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  externalAnchorEl: PropTypes.object,
  filterEnd: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  filterStart: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onClose: PropTypes.func,
};

export default memo(ColumnDateFilterMenu);
